import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Autocomplete,
    TextField,
    IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../context/AppContext";
import EditableForm from "../EditableForm"; // Assuming this form component handles the fields dynamically
import { createProject, fetchProjectCompanies, updateProjectApi } from "../../api/projects";
import CreateCompanyDialog from "./CreateCompanyDialog";
import AddIcon from "@mui/icons-material/Add"; // Import Add Icon
import theme from "../../utils/Theme";

const CreateProjectDialog = ({ open, onClose, onSuccess, project = null }) => {
    const { t } = useTranslation();
    const { setIsLoading } = useAppContext();
    const [error, setError] = useState(null);
    const [companies, setCompanies] = useState([]); // State to hold companies data
    const [openCreateCompanyDialog, setOpenCreateCompanyDialog] = useState(false); // State to open/close CreateCompanyDialog

    const [form, setForm] = useState({
        title: "",
        client_id: "",
        created_by_company_id: "",
        deadline: "",
        amount: 0,
        is_template: 0,
        status: "discussion",
    });

    useEffect(() => {
        console.log('form: ', form);
    }, [form])

    useEffect(() => {
        if (project) {
            setForm({
                title: project.title || "",
                client_id: project.client_id || "",
                created_by_company_id: project.created_by_company_id || "",
                deadline: project.deadline?.split("T")[0] || "",
                amount: project.amount || 0,
                is_template: project.is_template || 0,
                status: project.status || "discussion",
            });
        }
    }, [project]);

    useEffect(() => {
        const fetchCompanies = async () => {
            console.log('fetching companis')
            setIsLoading(true);
            try {
                const response = await fetchProjectCompanies();
                setCompanies(response);
            } catch (err) {
                console.error("Error fetching companies", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCompanies();
    }, [setIsLoading]);

    const handleSubmit = async (data) => {
        setIsLoading(true);
        try {
            if (project?.id) {
                await updateProjectApi(project.id, data);
            } else {
                await createProject(data);
            }
            setError(null);
            onSuccess?.();
            setForm({
                title: "",
                client_id: "",
                created_by_company_id: "",
                deadline: "",
                amount: 0,
                is_template: 0,
                status: "discussion",
            });
            onClose();
        } catch (err) {
            console.error("Save error", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const fields = [
        { name: "title", label: t("common.title"), required: true },
        { name: "deadline", label: t("fields.deadline"), type: "date" },
        { name: "amount", label: t("fields.amount"), type: "number" },
    ];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ color: "#000" }}>
                {project ? t("projects.edit_project") : t("projects.create_project")}
            </DialogTitle>
            <DialogContent sx={{ paddingTop: "5px !important" }}>
                {error && (
                    <Box mb={2}>
                        <Box
                            sx={{
                                bgcolor: "#fdecea",
                                color: "#b71c1c",
                                px: 2,
                                py: 1.5,
                                borderRadius: 1,
                                fontSize: 14,
                                fontWeight: 500,
                            }}
                        >
                            {t(`${error}`) || error}
                        </Box>
                    </Box>
                )}

                {/* Autocomplete for client selection */}
                <Box sx={{ width: '100%', display: "flex", alignItems: "center", marginBottom: '16px' }}>
                    <Autocomplete
                        fullWidth
                        value={companies?.find(company => company.id === form.client_id) || null}
                        onChange={(event, newValue) => {
                            setForm({ ...form, client_id: newValue?.id || "" });
                        }}
                        options={companies}
                        getOptionLabel={(option) => option.name || ""}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("common.client")}
                                fullWidth
                                required
                                sx={{
                                    // Ensures the background matches
                                    '& .MuiInputBase-root': {
                                        backgroundColor: "transparent",
                                    },
                                    // Input border and color matching
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: "#999", // Default border color
                                        },
                                        '&:hover fieldset': {
                                            borderColor: "#666", // Border color on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: "#333", // Border color when focused
                                        },
                                    },
                                    // Input text color
                                    '& .MuiInputBase-input': {
                                        color: "#000", // Input text color
                                    },
                                    // Label color
                                    '& .MuiInputLabel-root': {
                                        color: "#666", // Default label color
                                        "&.Mui-focused": {
                                            color: "#000", // Label color when focused
                                        },
                                    },
                                    // Dropdown icon color
                                    '& .MuiAutocomplete-popupIndicator': {
                                        color: "#000", // Arrow color
                                    },
                                    // Dropdown option text color
                                    '& .MuiAutocomplete-option': {
                                        color: "#000", // Text color in the dropdown
                                        '&:hover': {
                                            backgroundColor: "#f5f5f5", // Optional hover effect for options
                                        },
                                    },
                                }}
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        disableClearable
                        renderOption={(props, option) => {
                            const { title, color } = option;
                            return (
                                <span {...props} key={`option-${option.name}`} style={{ color: 'black' }}>
                                    {option.name}
                                </span>
                            );
                        }}
                    />
                    <IconButton
                        onClick={() => setOpenCreateCompanyDialog(true)}
                        sx={{
                            ml: 1,
                            backgroundColor: theme.palette.sidebar.background,
                            color: "white",
                            '&:hover': {
                                backgroundColor: "#004d40",
                            },
                        }}
                    >
                        <AddIcon />
                    </IconButton>
                </Box>

                {/* Editable form for other fields */}
                <EditableForm
                    data={form}
                    fields={fields}
                    onChange={setForm}
                    onSubmit={handleSubmit}
                    hideSubmitButton={true}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ color: "#000" }}>
                    {t("actions.cancel")}
                </Button>
                <Button onClick={() => handleSubmit(form)} sx={{ backgroundColor: theme.palette.sidebar.background, color: "white", padding: '10px' }}>
                    {project ? t("actions.update") : t("actions.create")}
                </Button>
            </DialogActions>

            <CreateCompanyDialog
                open={openCreateCompanyDialog}
                onClose={() => setOpenCreateCompanyDialog(false)}
                onSuccess={() => {
                    // Fetch companies again after adding a new company
                    setOpenCreateCompanyDialog(false);
                    const fetchCompanies = async () => {
                        setIsLoading(true);
                        try {
                            const response = await fetchProjectCompanies();
                            setCompanies(response);
                        } catch (err) {
                            console.error("Error fetching companies", err);
                        } finally {
                            setIsLoading(false);
                        }
                    };
                    fetchCompanies();
                }}
            />
        </Dialog>
    );
};

export default CreateProjectDialog;
